import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ContactForm from '../components/ContactForm';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Über uns', href: '/ueber-uns' },
    { name: 'Kontakt', href: '/kontakt' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const isActivePage = (href) => location.pathname === href;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? 'bg-white shadow-lg'
          : 'bg-white/95 backdrop-blur-sm shadow-md'
          }`}
      >
        {/* Desktop Header */}
        <div className="hidden lg:block">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between h-24 lg:h-28">
              {/* Logo */}
              <Link to="/" className="flex-shrink-0">
                <img
                  src="/logo/WithoutBackgroundLogo.png"
                  alt="ARES WOHNBAU Logo"
                  className="h-20 lg:h-24 w-auto"
                />
              </Link>

              {/* Navigation Links */}
              <div className="flex items-center space-x-6 lg:space-x-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`px-4 py-2 rounded-lg transition-all duration-300 text-sm lg:text-base font-medium ${isActivePage(link.href)
                      ? 'text-bronze-700 bg-bronze-100'
                      : 'text-gray-700 hover:text-bronze-600 hover:bg-gray-50'
                      }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>

              {/* CTA Button */}
              <button
                onClick={() => setIsContactFormOpen(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-bronze-500 to-bronze-600 hover:from-bronze-600 hover:to-bronze-700 text-white px-6 py-3 rounded-xl transition-all duration-300 font-medium shadow-lg hover:shadow-xl"
              >
                Beratung
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* ========================================= */}
        {/* MOBILE HEADER - Komplett neu und perfekt */}
        {/* ========================================= */}
        <div className="lg:hidden w-full">
          <div className="px-6 sm:px-8">
            <div className="h-24 flex items-center justify-between">

              {/* Logo - Links */}
              <Link to="/" className="flex-shrink-0">
                <img
                  src="/logo/WithoutBackgroundLogo.png"
                  alt="ARES WOHNBAU"
                  className="h-20 sm:h-24 w-auto object-contain"
                />
              </Link>

              {/* Rechte Seite - Phone Icon + Hamburger Menü */}
              <div className="flex items-center gap-4 mr-16 sm:mr-20">

                {/* Phone Icon Button */}
                <button
                  onClick={() => setIsContactFormOpen(true)}
                  className="text-gray-700 hover:text-bronze-600 transition-colors p-2"
                  aria-label="Kontakt aufnehmen"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </button>

                {/* Hamburger Menü Button - Style wie im Bild */}
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className={`w-11 h-11 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${isOpen
                    ? 'bg-bronze-500 border-bronze-500 text-white'
                    : 'bg-white border-gray-800 text-gray-800'
                    }`}
                  aria-label="Menü öffnen"
                >
                  {isOpen ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menü */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden bg-white border-t border-gray-100 shadow-2xl overflow-hidden"
            >
              <div className="pl-6 pr-16 py-6 sm:pl-8 sm:pr-20">
                {/* Navigation Links - 2x2 Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.name}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={`py-3 px-4 rounded-xl text-center text-sm font-semibold transition-all duration-200 ${isActivePage(link.href)
                        ? 'bg-bronze-500 text-white shadow-lg'
                        : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-bronze-300 hover:bg-bronze-50'
                        }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>

                {/* CTA Button - Volle Breite */}
                <button
                  onClick={() => {
                    setIsContactFormOpen(true);
                    setIsOpen(false);
                  }}
                  className="w-full bg-gradient-to-r from-bronze-500 to-bronze-600 hover:from-bronze-600 hover:to-bronze-700 text-white py-4 rounded-xl font-semibold text-sm sm:text-base flex items-center justify-center gap-2 shadow-xl transition-all duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Kostenloses Beratungsgespräch
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Contact Form Modal */}
      <ContactForm
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
      />
    </>
  );
};

export default Navbar;

